/**
 * Hook para buscar dados públicos de uma empresa via CNPJ usando a
 * BrasilAPI (https://brasilapi.com.br/api/cnpj/v1/<cnpj>).
 *
 * **Por que BrasilAPI**: gratuita, sem autenticação, alimentada por
 * dados públicos da Receita Federal. Rate limit fair-use sem número
 * fixo — em cadastros manuais esporádicos (Karla) e no form público
 * de onboarding é mais que suficiente. Se algum dia for gargalo,
 * trocamos pelo endpoint oficial `receitaws.com.br` (precisa chave
 * paga) ou um cache server-side.
 *
 * **Reutilizado em 2 lugares**:
 *   - `NewLeadDialog.tsx` (este PR) — Karla cadastra lead, autofill
 *     preenche razão social, endereço, telefone.
 *   - `/onboarding/<token>` (PR-M1-01b) — cliente preenche questionário
 *     público, mesmo autofill.
 *
 * Implementação: React Query com cache por CNPJ, debounce via enabled
 * apenas quando CNPJ é 14 dígitos. Cache por 24h (dados da Receita
 * raramente mudam).
 */
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

/** Subset do retorno BrasilAPI que nos interessa. Ignora campos que
 * não usamos (CNAE secundários, sócios, datas, etc.) via passthrough. */
const brasilApiCnpjSchema = z
  .object({
    cnpj: z.string(),
    razao_social: z.string().nullable().optional(),
    nome_fantasia: z.string().nullable().optional(),
    descricao_situacao_cadastral: z.string().nullable().optional(),
    cep: z.string().nullable().optional(),
    logradouro: z.string().nullable().optional(),
    numero: z.string().nullable().optional(),
    complemento: z.string().nullable().optional(),
    bairro: z.string().nullable().optional(),
    municipio: z.string().nullable().optional(),
    uf: z.string().nullable().optional(),
    ddd_telefone_1: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
  })
  .passthrough();

export type CnpjLookupResult = z.infer<typeof brasilApiCnpjSchema>;

/** Normaliza string de CNPJ removendo máscara e caracteres não-dígito.
 * Expõe como utilitário para uso em validação/submit além do hook. */
export function normalizeCnpj(input: string | null | undefined): string {
  if (!input) return '';
  return input.replace(/\D/g, '');
}

/** Aplica máscara visual `00.000.000/0000-00` ao CNPJ. Não valida. */
export function formatCnpj(cnpj: string | null | undefined): string {
  const digits = normalizeCnpj(cnpj);
  if (digits.length !== 14) return cnpj ?? '';
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

/**
 * Hook para o autofill. Retorna os dados do CNPJ quando o input tem
 * 14 dígitos válidos; caso contrário `isIdle` = true e não faz fetch.
 *
 * Estado exposto segue o padrão React Query: `isLoading`, `isError`,
 * `error`, `data`. O consumidor decide onde mostrar feedback.
 */
export function useCnpjLookup(cnpjInput: string | null | undefined) {
  const normalized = normalizeCnpj(cnpjInput);
  const enabled = normalized.length === 14;

  return useQuery({
    queryKey: ['cnpj-lookup', normalized],
    enabled,
    // Dados da Receita raramente mudam — 24h de cache é conservador e
    // suficiente pro fluxo de cadastro.
    staleTime: 24 * 60 * 60 * 1_000,
    retry: 1,
    queryFn: async (): Promise<CnpjLookupResult> => {
      const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${normalized}`, {
        headers: { accept: 'application/json' },
      });
      if (response.status === 404) {
        throw new Error('CNPJ não encontrado na Receita Federal.');
      }
      if (response.status === 429) {
        throw new Error('Muitas consultas de CNPJ em seguida. Aguarde alguns segundos.');
      }
      if (!response.ok) {
        throw new Error(`Falha ao consultar CNPJ (HTTP ${response.status}).`);
      }
      const raw = await response.json();
      return brasilApiCnpjSchema.parse(raw);
    },
  });
}
